import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Download, ShieldCheck, AlertTriangle, Check } from "lucide-react";

/**
 * One-time backup-codes display page.
 *
 * Shown right after a user's first successful 2FA sign-in
 * (when the backend reports `backup_codes_missing: true`).
 * Codes are auto-generated on mount, shown once, and the user must
 * explicitly acknowledge that they have saved them before continuing.
 */
export default function BackupCodesSetup() {
  const nav = useNavigate();
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.post("/auth/2fa/backup-codes/regenerate");
        if (!cancel) setCodes(r.data.codes || []);
      } catch (e) {
        toast.error(toErrMsg(e, "Could not generate backup codes"));
      } finally {
        if (!cancel) setBusy(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      toast.success("Backup codes copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    } catch { toast.error("Copy failed — select and copy manually."); }
  };

  const download = () => {
    const blob = new Blob(
      [`AZEX Backup Codes\nGenerated: ${new Date().toISOString()}\n\n` +
       codes.map((c, i) => `${(i + 1).toString().padStart(2, "0")}. ${c}`).join("\n") +
       `\n\nEach code works exactly once. Store securely — treat like a password.\n`],
      { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "azex-backup-codes.txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-lg" data-testid="backup-codes-setup">
        <Card className="border-emerald-200 dark:border-emerald-900">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 text-emerald-600">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">AZEX · Two-factor recovery</span>
            </div>
            <CardTitle className="mt-1 text-xl">Save your backup codes</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              These <b>10 single-use codes</b> let you sign in if you lose access to your email.
              This is the <b>only time</b> we'll show them. Store them somewhere safe — a password manager
              works best.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {busy ? (
              <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                Generating codes…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 rounded-lg border bg-slate-50 p-4 font-mono text-sm dark:bg-slate-900 dark:border-slate-800"
                     data-testid="backup-codes-list">
                  {codes.map((c, i) => (
                    <div key={c} className="rounded bg-white px-3 py-2 tracking-widest text-slate-800 dark:bg-slate-950 dark:text-slate-100">
                      <span className="me-2 text-[10px] text-muted-foreground">{(i + 1).toString().padStart(2, "0")}</span>{c}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={copyAll} data-testid="backup-copy" className="flex-1">
                    {copied ? <Check className="me-1 h-4 w-4 text-emerald-600" /> : <Copy className="me-1 h-4 w-4" />}
                    {copied ? "Copied" : "Copy all"}
                  </Button>
                  <Button variant="outline" onClick={download} data-testid="backup-download" className="flex-1">
                    <Download className="me-1 h-4 w-4" /> Download .txt
                  </Button>
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertTriangle className="me-1 inline h-3 w-3" />
                  You <b>will not</b> see these codes again. If you lose them, you'll need a Partner or
                  Platform Administrator to reset your 2FA.
                </div>

                <label className="flex items-start gap-2 pt-1 text-sm">
                  <input type="checkbox" className="mt-1"
                         checked={confirmed}
                         onChange={(e) => setConfirmed(e.target.checked)}
                         data-testid="backup-confirm-checkbox" />
                  <span>I have saved my backup codes in a safe place.</span>
                </label>

                <Button className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => nav("/", { replace: true })}
                        disabled={!confirmed}
                        data-testid="backup-continue">
                  Continue to AZEX
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
